// =========================
// Help — BL-037 Story 37.2 (Help Center)
//
// A lightweight, STATIC, self-service help surface for the controlled-
// onboarding cohort. No backend, no CMS, no chat, no ticketing — just
// onboarding-focused content that answers the most common questions so a new
// user can succeed without raising a support request.
//
// Content guardrails:
//   • Reinforces ADR-044 — "Intelligence, not advice"; the user owns the
//     investment decision. Uses Opportunity Signal / Opportunity Score labels.
//   • Respects ADR-029 — explains qualification at a HIGH LEVEL only; never
//     exposes the engine's weights, formula, or proprietary scoring internals.
//   • Reuses the Story 37.1 /support surface for all feedback/bug/feature/
//     contact actions (no duplicated submission functionality here).
//
// Design-system tokens only (no hex — CLAUDE.md). Responsive single-column
// content within a bounded container.
// =========================

import React from 'react';
import { Link } from 'react-router-dom';

// Required FAQ topics (Story 37.2 Phase 3E). Intelligence-not-advice framing
// (ADR-044) and no proprietary internals (ADR-029).
const FAQS = [
  {
    q: 'How is data sourced?',
    a: 'Rentava combines licensed market data from authorized providers with its own ' +
       'derived intelligence. Where live coverage is thin, transparent fallback estimates ' +
       'are used so you always get a result — comparable counts and sources are shown in context.',
  },
  {
    q: 'Why do markets vary?',
    a: 'Short-term-rental and outdoor-hospitality performance is highly local. Occupancy, ' +
       'seasonality, regulation, and comparable supply differ by ZIP and asset type, so ' +
       'signals for two nearby properties can legitimately differ.',
  },
  {
    q: 'What does a score mean?',
    a: 'An Opportunity Score is a relative indicator that summarizes the available market, ' +
       'financial, and risk signals into a single at-a-glance read. It is an input to your ' +
       'thinking — not a verdict, and not a guarantee of any outcome.',
  },
  {
    q: 'Can Rentava make investment decisions for me?',
    a: 'No. Rentava provides intelligence, not advice. Every signal, score, and ' +
       'recommendation is informational. You are responsible for your own investment ' +
       'decisions and for your own due diligence.',
  },
  {
    q: 'How do I report an issue?',
    a: 'Use Help & Feedback to report a bug, request a feature, or ask a question — ' +
       'it goes straight to the Rentava team.',
  },
];

export default function Help() {
  const sectionStyle = {
    display: 'grid',
    gap: 'var(--space-3)',
    padding: 'var(--space-5)',
  };

  const h2Style = {
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--text-xl)',
    fontWeight: 'var(--weight-bold)',
    color: 'var(--color-white)',
    margin: 0,
  };

  const pStyle = {
    fontSize: 'var(--text-base)',
    color: 'var(--color-steel-lt)',
    lineHeight: 'var(--leading-normal)',
    margin: 0,
  };

  const triageItemStyle = {
    display: 'grid',
    gap: 'var(--space-1)',
    padding: 'var(--space-2) var(--space-4)',
  };

  return (
    <div
      style={{
        background: 'var(--color-navy)',
        minHeight: '100%',
        padding: 'var(--space-6)',
        color: 'var(--color-white)',
        fontFamily: 'var(--font-body)',
      }}
    >
      <main style={{ maxWidth: 760, margin: '0 auto', display: 'grid', gap: 'var(--space-5)' }}>
        <header>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-4xl)',
              fontWeight: 'var(--weight-black)',
              letterSpacing: 'var(--tracking-tight)',
              textTransform: 'uppercase',
              margin: 0,
              marginBottom: 'var(--space-2)',
            }}
          >
            Help Center
          </h1>
          <p style={{ ...pStyle, fontSize: 'var(--text-md)' }}>
            Everything you need to get started with Rentava — and how to reach us if you get stuck.
          </p>
        </header>

        {/* A. Getting Started */}
        <section className="card" style={sectionStyle} aria-labelledby="help-getting-started">
          <h2 id="help-getting-started" style={h2Style}>Getting Started</h2>
          <p style={pStyle}>
            <strong>Rentava is Opportunity Intelligence for RV and outdoor-hospitality investors.</strong>{' '}
            It takes a property or lead, runs it through market, financial, and risk signals, and helps
            you decide where to spend your time — surfacing opportunities, not just calculations.
          </p>
          <p style={pStyle}>
            <strong>What is Opportunity Intelligence?</strong> Instead of starting from a listing and
            crunching numbers, Rentava brings together live market data and its own derived intelligence
            so you can spot, qualify, and prioritize opportunities — including off-market ones.
          </p>
          <p style={pStyle}>
            <strong>How to begin:</strong> open the Deal Analyzer to analyze a specific property, or use
            Qualify to triage an opportunity before a deep dive. Saved work appears under My Analyses and
            your pipeline under Leads.
          </p>
        </section>

        {/* B. Opportunity Signals */}
        <section className="card" style={sectionStyle} aria-labelledby="help-signals">
          <h2 id="help-signals" style={h2Style}>Opportunity Signals</h2>
          <p style={pStyle}>
            An <strong>Opportunity Signal</strong> is Rentava&apos;s at-a-glance read on a property,
            summarized as an <strong>Opportunity Score</strong>. When you triage an opportunity, the
            signal resolves to one of three outcomes:
          </p>
          <div className="card" style={triageItemStyle}>
            <span style={{ fontWeight: 'var(--weight-bold)' }}>Pursue</span>
            <span style={pStyle}>The signals are strong — this opportunity is worth a closer look.</span>
          </div>
          <div className="card" style={triageItemStyle}>
            <span style={{ fontWeight: 'var(--weight-bold)' }}>Investigate</span>
            <span style={pStyle}>The signals are mixed — gather more information before committing time.</span>
          </div>
          <div className="card" style={triageItemStyle}>
            <span style={{ fontWeight: 'var(--weight-bold)' }}>Reject</span>
            <span style={pStyle}>The signals are weak or a gate fails — likely not worth pursuing now.</span>
          </div>
          <p style={pStyle}>
            <strong>Intelligence, not advice.</strong> Signals and scores are informational inputs to
            help you think — they are not financial advice and not a recommendation to buy or sell.
            You are responsible for your own investment decisions and due diligence.
          </p>
        </section>

        {/* C. Opportunity Qualification */}
        <section className="card" style={sectionStyle} aria-labelledby="help-qualification">
          <h2 id="help-qualification" style={h2Style}>Opportunity Qualification</h2>
          <p style={pStyle}>
            <strong>Purpose:</strong> Qualification is a fast triage step that answers a single question —
            <em> is this worth a deeper analysis?</em> — so you can focus your time on the opportunities
            most likely to pay off.
          </p>
          <p style={pStyle}>
            <strong>How it works (high level):</strong> you confirm a few regulatory basics and provide
            the location and key details; Rentava combines market, financial, and risk signals into an
            Opportunity Score and a Pursue / Investigate / Reject outcome. The exact scoring methodology
            is proprietary, but the inputs and the reasons behind a result are always shown.
          </p>
          <p style={pStyle}>
            <strong>What the result means:</strong> a result points you toward a next action — pursue a
            full Deal Analyzer run, gather more information, or move on. It is a starting point for your
            judgment, never a final decision.
          </p>
        </section>

        {/* D. Feedback & Support */}
        <section className="card" style={sectionStyle} aria-labelledby="help-feedback">
          <h2 id="help-feedback" style={h2Style}>Feedback &amp; Support</h2>
          <p style={pStyle}>
            Stuck, or have an idea? Everything goes through one place —{' '}
            <Link to="/support" style={{ color: 'var(--color-teal)', fontWeight: 'var(--weight-semibold)' }}>
              Help &amp; Feedback
            </Link>. Pick the category that fits:
          </p>
          <ul style={{ ...pStyle, paddingLeft: 'var(--space-5)', display: 'grid', gap: 'var(--space-1)' }}>
            <li><strong>Feedback</strong> — tell us what&apos;s working or what could be better.</li>
            <li><strong>Bug report</strong> — something looks broken or behaves unexpectedly.</li>
            <li><strong>Feature request</strong> — something you&apos;d like Rentava to do.</li>
            <li><strong>Support / question</strong> — you need help using the platform.</li>
          </ul>
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            <Link to="/support" className="btn btn-primary">Submit feedback</Link>
            <Link to="/support" className="btn btn-ghost">Report a bug</Link>
            <Link to="/support" className="btn btn-ghost">Request a feature</Link>
            <Link to="/support" className="btn btn-ghost">Contact support</Link>
          </div>
        </section>

        {/* E. FAQ */}
        <section className="card" style={sectionStyle} aria-labelledby="help-faq">
          <h2 id="help-faq" style={h2Style}>Frequently Asked Questions</h2>
          {FAQS.map((item) => (
            <div key={item.q} style={{ display: 'grid', gap: 'var(--space-1)' }}>
              <h3
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-md)',
                  fontWeight: 'var(--weight-bold)',
                  color: 'var(--color-white)',
                  margin: 0,
                }}
              >
                {item.q}
              </h3>
              <p style={pStyle}>{item.a}</p>
            </div>
          ))}
        </section>
      </main>
    </div>
  );
}
